import { useCallback } from "react";
import {
  OrganizationPublicId,
  PrivacyCenterNoticePublicId,
  PrivacyCenterPublicId,
  UUIDString,
} from "../../common/service/server/Types";
import {
  CreatePrivacyCenterNoticeSectionBatchRequest,
  PrivacyCenterNoticeDto,
  PrivacyCenterNoticeSectionDto,
  UpdatePrivacyCenterNoticeSectionRequest,
} from "../../common/service/server/dto/PrivacyCenterDto";
import { Api } from "../../common/service/Api";
import { useDataRequest } from "../../common/hooks/api";
import { NetworkRequestState } from "../../common/models";
import { useMutation } from "@tanstack/react-query";

export const usePrivacyCenterNotices = (
  organizationId: OrganizationPublicId,
  privacyCenterId: PrivacyCenterPublicId,
): [PrivacyCenterNoticeDto[], NetworkRequestState, () => void] => {
  const api = useCallback(
    () => Api.privacyCenter.notices.getNotices(organizationId, privacyCenterId),
    [organizationId, privacyCenterId],
  );

  const {
    result: notices,
    request,
    refresh: fetch,
  } = useDataRequest({
    queryKey: ["privacyCenterNotices", organizationId, privacyCenterId],
    api,
  });

  return [notices, request, fetch];
};

export const usePrivacyCenterNotice = (
  organizationId: OrganizationPublicId,
  privacyCenterId: PrivacyCenterPublicId,
  noticeId: string,
): [PrivacyCenterNoticeDto, NetworkRequestState, () => Promise<void>] => {
  const api = useCallback(() => {
    return Api.privacyCenter.notices.getNotice(organizationId, privacyCenterId, noticeId);
  }, [organizationId, privacyCenterId, noticeId]);

  const {
    result: notice,
    request,
    refresh: fetch,
  } = useDataRequest({
    queryKey: ["privacyCenterNotice", organizationId, privacyCenterId, noticeId],
    api,
    staleTime: 0,
    keepPreviousData: false,
    notReady: !organizationId || !privacyCenterId || !noticeId || noticeId === "",
  });

  return [notice, request, fetch];
};

export interface CreateSectionApiArgs {
  organizationId: OrganizationPublicId;
  privacyCenterId: PrivacyCenterPublicId;
  noticeId: PrivacyCenterNoticePublicId;
  createSectionBody: CreatePrivacyCenterNoticeSectionBatchRequest;
}
export const useCreateSectionApi = ({
  onSuccess,
}: {
  onSuccess: (data: PrivacyCenterNoticeSectionDto, args: CreateSectionApiArgs) => void;
}) => {
  const { mutateAsync: addSection, ...rest } = useMutation(
    ({ organizationId, privacyCenterId, noticeId, createSectionBody }: CreateSectionApiArgs) => {
      return Api.privacyCenter.notices.sections.createSection(
        organizationId,
        privacyCenterId,
        noticeId,
        createSectionBody,
      );
    },
    {
      onSuccess,
    },
  );

  return { addSection, ...rest };
};

export function useUpdateSectionApi() {
  const { mutateAsync: updateSection, ...rest } = useMutation({
    mutationFn: async ({
      organizationId,
      privacyCenterId,
      noticeId,
      sectionId,
      updateSectionBody,
    }: {
      organizationId: OrganizationPublicId;
      privacyCenterId: PrivacyCenterPublicId;
      noticeId: PrivacyCenterNoticePublicId;
      sectionId: UUIDString;
      updateSectionBody: UpdatePrivacyCenterNoticeSectionRequest;
    }) =>
      Api.section.updateSection(
        organizationId,
        privacyCenterId,
        noticeId,
        sectionId,
        updateSectionBody,
      ),
  });

  return { updateSection, ...rest };
}

interface DeleteSectionArgs {
  organizationId: OrganizationPublicId;
  privacyCenterId: PrivacyCenterPublicId;
  noticeId: PrivacyCenterNoticePublicId;
  sectionId: UUIDString;
}

export function useDeleteSectionApi() {
  const { mutateAsync: deleteSection, ...rest } = useMutation(
    ({ organizationId, privacyCenterId, noticeId, sectionId }: DeleteSectionArgs) =>
      Api.section.deleteSection(organizationId, privacyCenterId, noticeId, sectionId),
  );
  return { deleteSection, ...rest };
}
